import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from './apiConfig';

const fetchWithToken = async (url, options = {}) => {
  try {
    const token = await AsyncStorage.getItem('userToken');
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log(`Usando token: ${token.substring(0, 10)}...`);
    } else {
      console.log('No se encontró token de autenticación');
    }
    
    // Sucursal activa: si hay una seleccionada, se agrega _tenant_id a TODAS
    // las peticiones para que el backend opere sobre esa sucursal. El backend
    // solo lo respeta para admins del mismo grupo; para el resto lo ignora.
    let finalUrl = url;
    try {
      const sucursalId = await AsyncStorage.getItem('activeSucursalId');
      if (sucursalId) {
        finalUrl += (url.includes('?') ? '&' : '?') + '_tenant_id=' + sucursalId;
      }
    } catch (e) { /* noop */ }

    console.log(`Realizando petición a: ${API_URL}${finalUrl}`);
    if (options.body) {
      console.log('Datos enviados:', options.body);
    }

    const response = await fetch(`${API_URL}${finalUrl}`, {
      ...options,
      headers,
    });

    console.log(`Respuesta recibida - Status: ${response.status} ${response.statusText}`);

    // Intentamos parsear la respuesta como JSON
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
      console.log('Datos de respuesta:', JSON.stringify(data));
    } else {
      const text = await response.text();
      console.log('Respuesta no JSON:', text);
      data = { message: text };
    }

    // Manejar token inválido o expirado
    if (response.status === 401 || response.status === 403) {
      const errorMessage = data.message || `Error ${response.status}: ${response.statusText}`;

      // Si el error es relacionado con token inválido, limpiar la sesión
      if (errorMessage.includes('Token inválido') || errorMessage.includes('Token no proporcionado')) {
        console.log('Token inválido detectado, limpiando sesión...');
        await AsyncStorage.removeItem('userToken');
        await AsyncStorage.removeItem('user');

        // Crear un error especial para que el frontend pueda redirigir al login
        const authError = new Error(errorMessage);
        authError.isAuthError = true;
        throw authError;
      }

      console.error(`Error API (${response.status}):`, errorMessage);
      throw new Error(errorMessage);
    }

    if (!response.ok) {
      const errorMessage = data.message || `Error ${response.status}: ${response.statusText}`;
      const errorDetails = data.error || 'Sin detalles adicionales';
      console.error(`Error API (${response.status}):`, errorMessage);
      console.error(`Detalles del error:`, errorDetails);
      console.error(`Respuesta completa:`, JSON.stringify(data));

      // Crear un error más descriptivo
      const detailedError = new Error(errorMessage);
      detailedError.details = errorDetails;
      detailedError.fullResponse = data;
      throw detailedError;
    }
    
    return data;
  } catch (error) {
    console.error('API Error:', error);
    
    // Si es un error de autenticación, no modificar el mensaje
    if (error.isAuthError) {
      throw error;
    }
    
    // Mejorar los mensajes de error para problemas comunes
    if (error.message === 'Network request failed') {
      throw new Error('No se pudo conectar al servidor. Verifica tu conexión a internet y que el servidor esté en funcionamiento.');
    }
    
    throw error;
  }
};

const api = {
  // Método genérico GET
  get: async (endpoint) => {
    return await fetchWithToken(endpoint);
  },

  // Método genérico POST
  post: async (endpoint, data) => {
    return await fetchWithToken(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Autenticación
  login: async (email, password, tenantSlug = null) => {
    console.log(`Intentando iniciar sesion con: ${email}, tenant: ${tenantSlug || 'auto'}`);
    try {
      const body = { username: email, password };
      if (tenantSlug) body.tenant_slug = tenantSlug;

      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      console.log('Respuesta del servidor:', response.status);

      const data = await response.json();

      // Multi-tenant: el backend pide seleccionar empresa
      if (response.status === 300) {
        return { requireTenantSlug: true, tenants: data.tenants };
      }

      if (!response.ok) {
        throw new Error(data.message || 'Error al iniciar sesion');
      }

      console.log('Inicio de sesion exitoso:', data.user.email);
      return data;
    } catch (error) {
      console.error('Error en la peticion de login:', error);
      throw error;
    }
  },

  // Registro de empresa (publico)
  registrarEmpresa: async (data) => {
    const response = await fetch(`${API_URL}/tenant-registro/solicitar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.message);
    return result;
  },

  verificarEmail: async (email) => {
    const response = await fetch(`${API_URL}/tenant-registro/verificar/${encodeURIComponent(email)}`);
    return await response.json();
  },

  getEstadoSolicitud: async (id) => {
    const response = await fetch(`${API_URL}/tenant-registro/estado/${id}`);
    return await response.json();
  },

  getPlanesPublicos: async () => {
    const response = await fetch(`${API_URL}/tenant-registro/planes`);
    return await response.json();
  },

  // Super Admin
  superAdmin: {
    getDashboard: () => fetchWithToken('/super-admin/dashboard'),
    getTenants: (params = '') => fetchWithToken(`/super-admin/tenants${params ? '?' + params : ''}`),
    getTenant: (id) => fetchWithToken(`/super-admin/tenants/${id}`),
    updateTenant: (id, data) => fetchWithToken(`/super-admin/tenants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    activarTenant: (id) => fetchWithToken(`/super-admin/tenants/${id}/activar`, { method: 'PUT' }),
    suspenderTenant: (id) => fetchWithToken(`/super-admin/tenants/${id}/suspender`, { method: 'PUT' }),
    cancelarTenant: (id) => fetchWithToken(`/super-admin/tenants/${id}/cancelar`, { method: 'PUT' }),
    getSolicitudes: (estado = 'pendiente') => fetchWithToken(`/super-admin/solicitudes?estado=${estado}`),
    aprobarSolicitud: (id) => fetchWithToken(`/super-admin/solicitudes/${id}/aprobar`, { method: 'PUT' }),
    rechazarSolicitud: (id, notas) => fetchWithToken(`/super-admin/solicitudes/${id}/rechazar`, { method: 'PUT', body: JSON.stringify({ notas_admin: notas }) }),
    getPlanes: () => fetchWithToken('/super-admin/planes'),
    createPlan: (data) => fetchWithToken('/super-admin/planes', { method: 'POST', body: JSON.stringify(data) }),
    updatePlan: (id, data) => fetchWithToken(`/super-admin/planes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    getEstadisticas: () => fetchWithToken('/super-admin/estadisticas'),
  },

  // Sucursales (sub-tenant). Nota: estas llamadas NO deben llevar _tenant_id de
  // sucursal activa porque operan a nivel de grupo; el backend igual lo ignora.
  getSucursales: () => fetchWithToken('/sucursales'),
  getConsolidado: (periodo = 'este_mes') => fetchWithToken(`/sucursales/consolidado?periodo=${encodeURIComponent(periodo)}`),
  createSucursal: (data) => fetchWithToken('/sucursales', { method: 'POST', body: JSON.stringify(data) }),
  updateSucursal: (id, data) => fetchWithToken(`/sucursales/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  setSucursalEstado: (id, estado) => fetchWithToken(`/sucursales/${id}/estado`, { method: 'PATCH', body: JSON.stringify({ estado }) }),
  deleteSucursal: (id) => fetchWithToken(`/sucursales/${id}`, { method: 'DELETE' }),
  getUsuariosGrupo: () => fetchWithToken('/sucursales/usuarios'),
  moverUsuario: (id, tenant_id) => fetchWithToken(`/sucursales/usuarios/${id}/mover`, { method: 'PATCH', body: JSON.stringify({ tenant_id }) }),

  // Clientes
  getClientes: () => fetchWithToken('/clientes'),
  getClienteVentas: (clienteId) => fetchWithToken(`/clientes/${clienteId}/ventas`),
  createCliente: async (clienteData) => {
    console.log('Creando cliente con datos:', clienteData);
    try {
      const response = await fetchWithToken('/clientes', {
        method: 'POST',
        body: JSON.stringify(clienteData),
      });
      console.log('Respuesta de creación de cliente:', response);
      return response;
    } catch (error) {
      console.error('Error al crear cliente:', error);
      throw error;
    }
  },
  updateCliente: async (id, clienteData) => {
    console.log(`Actualizando cliente ID ${id} con datos:`, clienteData);
    try {
      const response = await fetchWithToken(`/clientes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(clienteData),
      });
      console.log('Respuesta de actualización de cliente:', response);
      return response;
    } catch (error) {
      console.error('Error al actualizar cliente:', error);
      throw error;
    }
  },
  
  deleteCliente: async (id) => {
    console.log(`Eliminando cliente ID ${id}`);
    try {
      const response = await fetchWithToken(`/clientes/${id}`, {
        method: 'DELETE',
      });
      console.log('Respuesta de eliminación de cliente:', response);
      return response;
    } catch (error) {
      console.error('Error al eliminar cliente:', error);
      throw error;
    }
  },
  
  // Productos
  getProductos: async () => {
    console.log('Obteniendo lista de productos');
    try {
      const response = await fetchWithToken('/productos');
      console.log('Productos obtenidos:', response);
      return response;
    } catch (error) {
      console.error('Error al obtener productos:', error);
      throw error;
    }
  },
  
  getProducto: async (id) => {
    console.log(`Obteniendo producto con ID: ${id}`);
    try {
      const response = await fetchWithToken(`/productos/${id}`);
      console.log('Respuesta de producto:', response);
      return response;
    } catch (error) {
      console.error(`Error al obtener producto ${id}:`, error);
      throw error;
    }
  },
  
  createProducto: async (productoData) => {
    console.log('Creando producto con datos:', productoData);
    try {
      const response = await fetchWithToken('/productos', {
        method: 'POST',
        body: JSON.stringify(productoData),
      });
      console.log('Respuesta de creación de producto:', response);
      return response;
    } catch (error) {
      console.error('Error al crear producto:', error);
      throw error;
    }
  },
  
  updateProducto: async (id, productoData) => {
    console.log(`Actualizando producto ID ${id} con datos:`, JSON.stringify(productoData));
    try {
      // Asegurarse de que el ID sea un número
      const numericId = parseInt(id);
      if (isNaN(numericId)) {
        throw new Error(`ID de producto inválido: ${id}`);
      }
      
      // Realizar la petición al servidor
      const response = await fetchWithToken(`/productos/${numericId}`, {
        method: 'PUT',
        body: JSON.stringify(productoData),
      });
      
      console.log('Respuesta de actualización de producto:', response);
      return response;
    } catch (error) {
      console.error('Error al actualizar producto:', error);
      throw error;
    }
  },
  
  // Obtener todas las categorías
  getCategorias: async () => {
    console.log('Obteniendo categorías');
    try {
      const response = await fetchWithToken('/categorias');
      console.log('Categorías obtenidas:', response);
      return response;
    } catch (error) {
      console.error('Error al obtener categorías:', error);
      throw error;
    }
  },
  
  // Crear una nueva categoría
  createCategoria: async (categoriaData) => {
    console.log('Creando nueva categoría:', JSON.stringify(categoriaData));
    try {
      const response = await fetchWithToken('/categorias', {
        method: 'POST',
        body: JSON.stringify(categoriaData),
      });
      console.log('Respuesta de creación de categoría:', response);
      return response;
    } catch (error) {
      console.error('Error al crear categoría:', error);
      throw error;
    }
  },

  updateCategoria: async (id, categoriaData) => {
    try {
      return await fetchWithToken(`/categorias/${id}`, {
        method: 'PUT',
        body: JSON.stringify(categoriaData),
      });
    } catch (error) {
      console.error(`Error al actualizar categoría ${id}:`, error);
      throw error;
    }
  },

  deleteCategoria: async (id) => {
    try {
      return await fetchWithToken(`/categorias/${id}`, { method: 'DELETE' });
    } catch (error) {
      console.error(`Error al eliminar categoría ${id}:`, error);
      throw error;
    }
  },

  // ===== Presentaciones (catálogo global del tenant) =====
  getPresentaciones: async () => {
    try {
      return await fetchWithToken('/presentaciones');
    } catch (error) {
      console.error('Error al obtener presentaciones:', error);
      throw error;
    }
  },
  createPresentacion: async (data) => {
    try {
      return await fetchWithToken('/presentaciones', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Error al crear presentación:', error);
      throw error;
    }
  },
  updatePresentacion: async (id, data) => {
    try {
      return await fetchWithToken(`/presentaciones/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error(`Error al actualizar presentación ${id}:`, error);
      throw error;
    }
  },
  deletePresentacion: async (id) => {
    try {
      return await fetchWithToken(`/presentaciones/${id}`, { method: 'DELETE' });
    } catch (error) {
      console.error(`Error al eliminar presentación ${id}:`, error);
      throw error;
    }
  },

  // ===== Presentaciones vinculadas a un producto =====
  getPresentacionesProducto: async (productoId) => {
    try {
      return await fetchWithToken(`/productos/${productoId}/presentaciones`);
    } catch (error) {
      console.error(`Error al obtener presentaciones del producto ${productoId}:`, error);
      throw error;
    }
  },
  addPresentacionProducto: async (productoId, data) => {
    try {
      return await fetchWithToken(`/productos/${productoId}/presentaciones`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Error al asignar presentación:', error);
      throw error;
    }
  },
  updatePresentacionProducto: async (productoId, ppId, data) => {
    try {
      return await fetchWithToken(`/productos/${productoId}/presentaciones/${ppId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Error al actualizar presentación del producto:', error);
      throw error;
    }
  },
  removePresentacionProducto: async (productoId, ppId) => {
    try {
      return await fetchWithToken(`/productos/${productoId}/presentaciones/${ppId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error al desasignar presentación:', error);
      throw error;
    }
  },
  
  deleteProducto: async (id) => {
    console.log(`Eliminando producto ID ${id}`);
    try {
      const response = await fetchWithToken(`/productos/${id}`, {
        method: 'DELETE',
      });
      console.log('Respuesta de eliminación de producto:', response);
      return response;
    } catch (error) {
      console.error('Error al eliminar producto:', error);
      throw error;
    }
  },
  
  // Proveedores
  getProveedores: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithToken(`/proveedores${query ? '?' + query : ''}`);
  },
  getProveedor: (id) => fetchWithToken(`/proveedores/${id}`),
  createProveedor: (proveedorData) => {
    return fetchWithToken('/proveedores', {
      method: 'POST',
      body: JSON.stringify(proveedorData),
    });
  },
  updateProveedor: (id, data) => fetchWithToken(`/proveedores/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProveedor: (id) => fetchWithToken(`/proveedores/${id}`, { method: 'DELETE' }),

  // Compras (Órdenes de Compra)
  getCompras: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithToken(`/compras${query ? '?' + query : ''}`);
  },
  getCompra: (id) => fetchWithToken(`/compras/${id}`),
  getComprasResumen: () => fetchWithToken('/compras/resumen/estados'),
  createCompra: (data) => fetchWithToken('/compras', { method: 'POST', body: JSON.stringify(data) }),
  updateCompra: (id, data) => fetchWithToken(`/compras/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  confirmarCompra: (id) => fetchWithToken(`/compras/${id}/confirmar`, { method: 'PUT' }),
  recibirCompra: (id, data) => fetchWithToken(`/compras/${id}/recibir`, { method: 'PUT', body: JSON.stringify(data) }),
  cancelarCompra: (id) => fetchWithToken(`/compras/${id}/cancelar`, { method: 'PUT' }),

  // Ventas
  getVentas: () => fetchWithToken('/ventas'),
  getVenta: (id) => fetchWithToken(`/ventas/${id}`),
  createVenta: (ventaData) => {
    return fetchWithToken('/ventas', {
      method: 'POST',
      body: JSON.stringify(ventaData),
    });
  },
  cancelarVenta: (id) => {
    return fetchWithToken(`/ventas/${id}/cancelar`, {
      method: 'PUT'
    });
  },

  // ===== Borradores de venta =====
  getBorradoresVenta: () => fetchWithToken('/ventas?estado=borrador'),
  guardarBorradorVenta: (ventaData) => {
    // Crear nuevo borrador
    return fetchWithToken('/ventas', {
      method: 'POST',
      body: JSON.stringify({ ...ventaData, as_borrador: true }),
    });
  },
  actualizarBorradorVenta: (id, ventaData) => {
    return fetchWithToken(`/ventas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(ventaData),
    });
  },
  procesarBorradorVenta: (id, ajustes = {}) => {
    return fetchWithToken(`/ventas/${id}/procesar`, {
      method: 'POST',
      body: JSON.stringify(ajustes),
    });
  },
  eliminarBorradorVenta: (id) => {
    return fetchWithToken(`/ventas/${id}`, { method: 'DELETE' });
  },

  // Pedidos - Sistema de estados
  getEstados: () => fetchWithToken('/pedidos/estados'),
  getPedidosPorEstado: (estado) => fetchWithToken(`/pedidos/por-estado/${estado}`),
  getResumenEstados: () => fetchWithToken('/pedidos/resumen-estados'),
  cambiarEstadoPedido: (id, data) => {
    return fetchWithToken(`/pedidos/${id}/estado`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  getHistorialPedido: (id) => fetchWithToken(`/pedidos/${id}/historial`),

  // Bodega
  getBodegaPendientes: () => fetchWithToken('/bodega/pedidos-pendientes'),
  getBodegaPreparados: () => fetchWithToken('/bodega/pedidos-preparados'),
  getBodegaResumen: () => fetchWithToken('/bodega/resumen-diario'),
  getBodegaChecklist: (ventaId) => fetchWithToken(`/bodega/checklist/${ventaId}`),
  verificarItemBodega: (itemId, data) => {
    return fetchWithToken(`/bodega/checklist/${itemId}/verificar`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  desverificarItemBodega: (itemId) => {
    return fetchWithToken(`/bodega/checklist/${itemId}/desverificar`, {
      method: 'PUT',
    });
  },
  confirmarPedidoBodega: (ventaId, data) => {
    return fetchWithToken(`/bodega/confirmar/${ventaId}`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  },
  
  // Lotes de productos
  getLotesProducto: (productoId) => fetchWithToken(`/lotes/producto/${productoId}`),
  getLotesPorVencer: (dias = 30) => fetchWithToken(`/lotes/por-vencer?dias=${dias}`),
  getLotesVencidos: () => fetchWithToken('/lotes/vencidos'),
  getLotesResumen: () => fetchWithToken('/lotes/resumen'),
  createLote: (data) => fetchWithToken('/lotes', { method: 'POST', body: JSON.stringify(data) }),
  updateLote: (id, data) => fetchWithToken(`/lotes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Conteos de inventario
  getConteos: () => fetchWithToken('/conteos'),
  getConteo: (id) => fetchWithToken(`/conteos/${id}`),
  createConteo: (data) => fetchWithToken('/conteos', { method: 'POST', body: JSON.stringify(data || {}) }),
  registrarConteoDetalle: (conteoId, detalleId, data) => fetchWithToken(`/conteos/${conteoId}/detalle/${detalleId}`, { method: 'PUT', body: JSON.stringify(data) }),
  completarConteo: (id, data) => fetchWithToken(`/conteos/${id}/completar`, { method: 'POST', body: JSON.stringify(data || {}) }),

  // Productos - búsqueda por código de barras
  buscarPorCodigoBarras: (codigo) => fetchWithToken(`/productos/barcode/${codigo}`),

  // Cotizaciones
  getCotizaciones: () => fetchWithToken('/cotizaciones'),
  getCotizacion: (id) => fetchWithToken(`/cotizaciones/${id}`),
  createCotizacion: (data) => {
    return fetchWithToken('/cotizaciones', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  convertirCotizacion: (id, data) => {
    return fetchWithToken(`/cotizaciones/${id}/convertir`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  },
  cancelarCotizacion: (id) => {
    return fetchWithToken(`/cotizaciones/${id}/cancelar`, {
      method: 'PUT',
    });
  },

  // Repartidor
  getRepartidorPreparados: () => fetchWithToken('/repartidor/pedidos-preparados'),
  getRepartidorEnRuta: () => fetchWithToken('/repartidor/pedidos-en-ruta'),
  getRepartidorEntregados: () => fetchWithToken('/repartidor/pedidos-entregados'),
  getRepartidorResumen: () => fetchWithToken('/repartidor/resumen-diario'),
  getRepartidorDetalle: (ventaId) => fetchWithToken(`/repartidor/detalle/${ventaId}`),
  iniciarEntregaRepartidor: (ventaId) => {
    return fetchWithToken(`/repartidor/iniciar-entrega/${ventaId}`, {
      method: 'POST',
    });
  },
  confirmarEntregaRepartidor: (ventaId, data) => {
    return fetchWithToken(`/repartidor/confirmar-entrega/${ventaId}`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  },
  completarVentaRepartidor: (ventaId, data) => {
    return fetchWithToken(`/repartidor/completar/${ventaId}`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  },

  // Devoluciones
  getDevoluciones: () => {
    console.log('Obteniendo devoluciones');
    return fetchWithToken('/devoluciones');
  },
  
  getDevolucion: (id) => {
    console.log(`Obteniendo devolución con ID: ${id}`);
    return fetchWithToken(`/devoluciones/${id}`);
  },
  
  createDevolucion: async (devolucionData) => {
    console.log('Creando devolución con datos:', devolucionData);
    
    // 1. Crear la devolución
    const devolucionResponse = await fetchWithToken('/devoluciones', {
      method: 'POST',
      body: JSON.stringify(devolucionData),
    });
    
    if (!devolucionResponse.id) {
      throw new Error('Error al crear la devolución: No se recibió ID de devolución');
    }
    
    // 2. DESACTIVADO: El sistema automático de devoluciones ya maneja todo
    // try {
    //   await fetchWithToken(`/ventas/${devolucionData.venta_id}/actualizar-por-devolucion`, {
    //     method: 'PUT',
    //     body: JSON.stringify({
    //       items_devueltos: devolucionData.items,
    //       devolucion_id: devolucionResponse.id
    //     }),
    //   });
    // } catch (error) {
    //   console.error('Error al actualizar la venta:', error);
    //   // No lanzamos error para no interrumpir el flujo, pero lo registramos
    // }
    
    // 3. DESACTIVADO: El sistema automático de devoluciones ya maneja el stock
    // try {
    //   // Asegurarnos de que cada item tenga la información necesaria del producto y unidad
    //   const itemsConInfo = devolucionData.items.map(item => ({
    //     ...item,
    //     // Asegurarnos de que estos campos estén presentes
    //     producto_nombre: item.producto_nombre || 'Producto sin nombre',
    //     producto_codigo: item.producto_codigo || `PROD-${item.producto_id}`,
    //     producto_id: parseInt(item.producto_id),
    //     // Información de la unidad - siempre usar la unidad base (ID 1) para evitar problemas
    //     unidad_id: 1, // Usar siempre 1 como la unidad base para evitar problemas con unidades no existentes
    //     unidad_nombre: 'Unidad'
    //   }));
    //   
    //   console.log('Enviando items para actualizar stock:', JSON.stringify(itemsConInfo, null, 2));
    //   
    //   await fetchWithToken('/productos/devolucion/actualizar-stock', {
    //     method: 'PUT',
    //     body: JSON.stringify({
    //       items: itemsConInfo,
    //       devolucion_id: devolucionResponse.id,
    //       venta_id: devolucionData.venta_id // Añadimos el ID de la venta para poder obtener las unidades correctas
    //     }),
    //   });
    // } catch (error) {
    //   console.error('Error al actualizar el stock:', error);
    //   // No lanzamos error para no interrumpir el flujo, pero lo registramos
    // }
    
    return devolucionResponse;
  },
  
  // Cierres del día
  getCierres: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithToken(`/cierres${query ? '?' + query : ''}`);
  },
  getCierre: (id) => fetchWithToken(`/cierres/${id}`),
  getCierrePreview: (tipo, fecha) => fetchWithToken(`/cierres/preview/${tipo}${fecha ? '?fecha=' + fecha : ''}`),
  createCierre: (cierreData) => fetchWithToken('/cierres', { method: 'POST', body: JSON.stringify(cierreData) }),
  completarCierre: (id) => fetchWithToken(`/cierres/${id}/completar`, { method: 'PUT' }),
  rechazarCierre: (id, data) => fetchWithToken(`/cierres/${id}/rechazar`, { method: 'PUT', body: JSON.stringify(data) }),
  autorizarDevolucionesCierre: (id, data) => fetchWithToken(`/cierres/${id}/autorizar-devoluciones`, { method: 'PUT', body: JSON.stringify(data) }),
  getConsolidadoDiario: (fecha) => fetchWithToken(`/cierres/consolidado/${fecha}`),
  ejecutarCierreAutomatico: () => fetchWithToken('/cierres/automatico', { method: 'POST' }),
  getCierreConfig: () => fetchWithToken('/cierres/config/general'),
  updateCierreConfig: (data) => fetchWithToken('/cierres/config/general', { method: 'PUT', body: JSON.stringify(data) }),
  
  // Reportes
  getVentasPorPeriodo: (fechaInicio, fechaFin) => {
    return fetchWithToken(`/reportes/ventas-por-periodo?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`);
  },
  getProductosMasVendidos: (fechaInicio, fechaFin) => {
    return fetchWithToken(`/reportes/productos-mas-vendidos?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`);
  },
  getInventarioBajo: () => fetchWithToken('/reportes/inventario-bajo'),
  
  // Rutas
  getRutas: () => fetchWithToken('/rutas'),
  getRuta: (id) => fetchWithToken(`/rutas/${id}`),
  createRuta: (rutaData) => {
    return fetchWithToken('/rutas', {
      method: 'POST',
      body: JSON.stringify(rutaData),
    });
  },
  updateRuta: (id, rutaData) => {
    return fetchWithToken(`/rutas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(rutaData),
    });
  },
  deleteRuta: (id) => {
    return fetchWithToken(`/rutas/${id}`, {
      method: 'DELETE',
    });
  },
  
  // Visitas
  getVisitas() {
    return fetchWithToken('/visitas');
  },
  createVisita(visitaData) {
    return fetchWithToken('/visitas', {
      method: 'POST',
      body: JSON.stringify(visitaData)
    });
  },
  updateVisita(id, visitaData) {
    return fetchWithToken(`/visitas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(visitaData)
    });
  },
  
  // Usuarios
  getUsuarios() {
    return fetchWithToken('/usuarios');
  },
  getUsuario(id) {
    return fetchWithToken(`/usuarios/${id}`);
  },
  createUsuario(usuarioData) {
    return fetchWithToken('/usuarios', {
      method: 'POST',
      body: JSON.stringify(usuarioData)
    });
  },
  updateUsuario(id, usuarioData) {
    return fetchWithToken(`/usuarios/${id}`, {
      method: 'PUT',
      body: JSON.stringify(usuarioData)
    });
  },
  deleteUsuario(id) {
    return fetchWithToken(`/usuarios/${id}`, {
      method: 'DELETE'
    });
  },

  // Perfil de usuario
  updateUserProfile(id, profileData) {
    return fetchWithToken(`/usuarios/${id}/perfil`, {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  },

  // Configuración del negocio
  getConfiguracion() {
    return fetchWithToken('/configuracion');
  },
  updateConfiguracion(configuracionData) {
    return fetchWithToken('/configuracion', {
      method: 'PUT',
      body: JSON.stringify(configuracionData)
    });
  },

  // ============ COMISIONES ============

  // Reglas de comisión
  getComisionReglas: () => fetchWithToken('/comisiones/reglas'),
  createComisionRegla: (data) => fetchWithToken('/comisiones/reglas', { method: 'POST', body: JSON.stringify(data) }),
  updateComisionRegla: (id, data) => fetchWithToken(`/comisiones/reglas/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteComisionRegla: (id) => fetchWithToken(`/comisiones/reglas/${id}`, { method: 'DELETE' }),

  // Comisiones
  getComisiones: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithToken(`/comisiones${query ? '?' + query : ''}`);
  },
  getComision: (id) => fetchWithToken(`/comisiones/${id}`),
  getComisionesVendedor: (id) => fetchWithToken(`/comisiones/vendedor/${id}`),
  getComisionesReporte: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithToken(`/comisiones/reporte${query ? '?' + query : ''}`);
  },
  getComisionesResumen: () => fetchWithToken('/comisiones/resumen'),
  calcularComision: (ventaId) => fetchWithToken(`/comisiones/calcular/${ventaId}`, { method: 'POST' }),
  aprobarComision: (id) => fetchWithToken(`/comisiones/${id}/aprobar`, { method: 'PUT' }),
  pagarComisiones: (data) => fetchWithToken('/comisiones/pagar', { method: 'POST', body: JSON.stringify(data) }),
  getComisionPagos: () => fetchWithToken('/comisiones/pagos'),

  // ============ PENALIZACIONES ============

  // Reglas de penalización
  getPenalizacionReglas: () => fetchWithToken('/penalizaciones/reglas'),
  createPenalizacionRegla: (data) => fetchWithToken('/penalizaciones/reglas', { method: 'POST', body: JSON.stringify(data) }),
  updatePenalizacionRegla: (id, data) => fetchWithToken(`/penalizaciones/reglas/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePenalizacionRegla: (id) => fetchWithToken(`/penalizaciones/reglas/${id}`, { method: 'DELETE' }),

  // Penalizaciones/Bonos
  getPenalizaciones: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithToken(`/penalizaciones${query ? '?' + query : ''}`);
  },
  createPenalizacion: (data) => fetchWithToken('/penalizaciones', { method: 'POST', body: JSON.stringify(data) }),
  aprobarPenalizacion: (id) => fetchWithToken(`/penalizaciones/${id}/aprobar`, { method: 'PUT' }),
  cancelarPenalizacion: (id) => fetchWithToken(`/penalizaciones/${id}/cancelar`, { method: 'PUT' }),
  getPenalizacionesReporte: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithToken(`/penalizaciones/reporte${query ? '?' + query : ''}`);
  },
  getPenalizacionesResumen: (usuarioId) => fetchWithToken(`/penalizaciones/resumen/${usuarioId}`),

  // ============ VOUCHERS ============

  getVouchers: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithToken(`/vouchers${query ? '?' + query : ''}`);
  },
  getVoucher: (id) => fetchWithToken(`/vouchers/${id}`),
  getVouchersResumen: () => fetchWithToken('/vouchers/resumen'),
  getVoucherEmpleados: () => fetchWithToken('/vouchers/empleados/lista'),
  getVoucherDatosEmpleado: (id) => fetchWithToken(`/vouchers/datos-empleado/${id}`),
  createVoucher: (data) => fetchWithToken('/vouchers', { method: 'POST', body: JSON.stringify(data) }),
  emitirVoucher: (id, data) => fetchWithToken(`/vouchers/${id}/emitir`, { method: 'PUT', body: JSON.stringify(data) }),
  firmarVoucher: (id, data) => fetchWithToken(`/vouchers/${id}/firmar`, { method: 'PUT', body: JSON.stringify(data) }),
  pagarVoucher: (id) => fetchWithToken(`/vouchers/${id}/pagar`, { method: 'PUT' }),
  anularVoucher: (id, data) => fetchWithToken(`/vouchers/${id}/anular`, { method: 'PUT', body: JSON.stringify(data) }),

  // Recalcular stock total de productos
  recalcularStockTotal(productoId = null) {
    const endpoint = productoId
      ? `/productos/recalcular-stock-total/${productoId}`
      : '/productos/recalcular-stock-total';

    return fetchWithToken(endpoint);
  },
};

export default api;
